import React from 'react';
import {
  Upload,
  Sparkles,
  Scissors,
  FileImage,
  Maximize,
  Minimize,
  Palette,
  Droplet,
  Scissors as EmbroideryIcon,
  Folder,
  Workflow,
  History,
  Download,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import type { ToolType } from '@/pages/ImageToolsHub';

interface ToolsSidebarProps {
  activeTool: ToolType;
  onToolChange: (tool: ToolType) => void;
}

const tools = [
  { id: 'upload' as ToolType, label: 'Upload', icon: Upload, group: 'Start' },
  { id: 'enhance' as ToolType, label: 'Enhance', icon: Sparkles, group: 'Basic Tools' },
  { id: 'background-remove' as ToolType, label: 'Remove Background', icon: Scissors, group: 'Basic Tools' },
  { id: 'format-convert' as ToolType, label: 'Convert Format', icon: FileImage, group: 'Basic Tools' },
  { id: 'resize' as ToolType, label: 'Resize & Crop', icon: Maximize, group: 'Basic Tools' },
  { id: 'compress' as ToolType, label: 'Compress', icon: Minimize, group: 'Basic Tools' },
  { id: 'filters' as ToolType, label: 'Filters & Effects', icon: Palette, group: 'Advanced Tools' },
  { id: 'watermark' as ToolType, label: 'Watermark', icon: Droplet, group: 'Advanced Tools' },
  { id: 'embroidery' as ToolType, label: 'Embroidery', icon: EmbroideryIcon, group: 'Advanced Tools' },
  { id: 'product-photo' as ToolType, label: 'Product Photos', icon: Sparkles, group: 'Specialized' },
  { id: 'social-media' as ToolType, label: 'Social Media', icon: Sparkles, group: 'Specialized' },
  { id: 'favicon-generator' as ToolType, label: 'Favicon & Icons', icon: FileImage, group: 'Specialized' },
  { id: 'projects' as ToolType, label: 'Projects', icon: Folder, group: 'Organization' },
  { id: 'presets' as ToolType, label: 'Presets', icon: Workflow, group: 'Organization' },
  { id: 'history' as ToolType, label: 'History', icon: History, group: 'Organization' },
  { id: 'export' as ToolType, label: 'Export', icon: Download, group: 'Organization' },
];

export const ToolsSidebar: React.FC<ToolsSidebarProps> = ({ activeTool, onToolChange }) => {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const groupedTools = tools.reduce((acc, tool) => {
    if (!acc[tool.group]) acc[tool.group] = [];
    acc[tool.group].push(tool);
    return acc;
  }, {} as Record<string, typeof tools>);

  return (
    <Sidebar className={isCollapsed ? 'w-14' : 'w-64'}>
      <div className="p-4 border-b">
        <SidebarTrigger className="ml-auto" />
        {!isCollapsed && (
          <h2 className="text-lg font-semibold mt-2">Image Tools</h2>
        )}
      </div>

      <SidebarContent>
        {Object.entries(groupedTools).map(([group, groupTools]) => (
          <SidebarGroup key={group}>
            {!isCollapsed && <SidebarGroupLabel>{group}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {groupTools.map((tool) => (
                  <SidebarMenuItem key={tool.id}>
                    <SidebarMenuButton
                      onClick={() => onToolChange(tool.id)}
                      isActive={activeTool === tool.id}
                      tooltip={isCollapsed ? tool.label : undefined}
                    >
                      <tool.icon className="h-4 w-4" />
                      {!isCollapsed && <span>{tool.label}</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};
